import {
  type ChangeEvent,
  type ReactElement,
  type Ref,
  isValidElement,
  useEffect,
  useRef,
} from "react";

import { cx, styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import type { IconData } from "simplydo/react/ui/icon";
import { Icon, isIconData } from "simplydo/react/ui/icon";

import { useFormControl } from "../form/FormFieldContext";
import { Label } from "../label";
import { UIProvider } from "../utils/UIContext";
import { CONTROL_HEIGHT, SIZE_FONT } from "../utils/colors";
import { FOCUS_RING } from "../utils/focus";
import type { InputProps, InputSize } from "./Input.types";

type InteractiveIconProps = { onClick?: unknown; link?: boolean };

function isInteractiveIcon(icon: InputProps["icon"]): boolean {
  if (!isValidElement(icon)) {
    return false;
  }
  const props = (icon as ReactElement<InteractiveIconProps>).props;
  return typeof props.onClick === "function" || !!props.link;
}

type FieldStyleProps = { $borderColor: string; $color: string };

const Wrapper = styled.div<{ $size?: InputSize }>`
  position: relative;
  display: inline-flex;
  width: auto;
  min-width: 10em;
  height: ${({ $size }) => CONTROL_HEIGHT[$size || "medium"]};
  align-items: stretch;
  vertical-align: middle;
  font-size: ${({ $size }) => ($size && SIZE_FONT[$size]) || SIZE_FONT.medium};

  &.is-fluid {
    display: flex;
    width: 100%;
    min-width: 0;
  }

  &.is-disabled {
    opacity: 0.45;
  }

  &.label-left {
    > ${Label} {
      height: auto;
      align-self: stretch;
      padding-top: 0;
      padding-bottom: 0;
      margin-left: 0;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  &.label-right {
    > ${Label} {
      height: auto;
      align-self: stretch;
      padding-top: 0;
      padding-bottom: 0;
      margin-left: 0;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
`;

const Field = styled.input<FieldStyleProps>`
  margin: 0;
  max-width: 100%;
  flex: 1 0 auto;
  outline: none;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
  text-align: left;
  line-height: 1.2em;
  font-family: inherit;
  padding: 0 1em;
  background: #ffffff;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  color: ${({ $color }) => $color};
  border-radius: 0.3rem;
  box-shadow: none;
  width: 100%;
  box-sizing: border-box;

  &.is-icon-left {
    padding-left: 2.75em;
  }

  &.is-icon-right {
    padding-right: 2.75em;
  }

  &.is-has-label {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &.is-has-action {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &:focus {
    background: #ffffff;
    color: rgba(0, 0, 0, 0.95);
  }

  &:focus-visible {
    ${FOCUS_RING}
  }

  &::placeholder {
    color: rgba(191, 191, 191, 0.87);
  }
`;

const IconSlot = styled.span<{ $interactive?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 2.75em;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${({ $interactive }) => ($interactive ? "auto" : "none")};
  cursor: ${({ $interactive }) => ($interactive ? "pointer" : undefined)};
  opacity: 0.4;
  z-index: 1;

  &.is-left {
    left: 0;
    right: auto;
  }

  > ${Icon} {
    margin: 0;
    line-height: 1;
    text-align: center;
    opacity: 1;
    cursor: inherit;
  }
`;

const Spinner = styled.span`
  position: absolute;
  top: 50%;
  right: 0.8em;
  width: 1em;
  height: 1em;
  margin-top: -0.5em;
  border: 0.15em solid rgba(0, 0, 0, 0.1);
  border-top-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  animation: sdo-input-spin 0.6s linear infinite;
  z-index: 1;

  @keyframes sdo-input-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ActionSlot = styled.div`
  display: flex;
  align-items: stretch;

  > * {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    margin: 0 !important;
  }

  /* Fit an attached action button to the input height. */
  > ${Button} {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    height: auto;
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 0.85em;
    padding-right: 0.85em;
  }

  > ${Button}.is-icon {
    padding-left: 0.65em;
    padding-right: 0.65em;
  }

  > ${Button}.is-labeled-icon {
    padding-left: 3.25em;
    padding-right: 0.85em;
  }

  > ${Button}.is-labeled-icon.is-labeled-icon-right {
    padding-left: 0.85em;
    padding-right: 3.25em;
  }
`;

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) {
    return;
  }
  if (typeof ref === "function") {
    ref(value);
  } else {
    (ref as { current: T | null }).current = value;
  }
}

const onWheel = (e: WheelEvent) => {
  e.preventDefault();
};

const InputComponent = (
  props: InputProps & { ref?: Ref<HTMLInputElement> },
) => {
  const iconPosition = props.iconPosition ?? "right";
  const type = props.type ?? "text";
  const labelPosition = props.labelPosition ?? "left";
  const {
    ref,
    fluid,
    icon,
    action,
    loading,
    size,
    value,
    defaultValue,
    name,
    onChange,
    label,
    placeholder,
    disabled,
    error,
    readOnly,
    className,
    style,
    onClick,
    onMouseEnter,
    onFocus,
    onBlur,
    onKeyDown,
    onKeyDownCapture,
    onKeyPress,
    id,
    autoFocus,
    autoComplete,
    tabIndex,
    maxLength,
    min,
    max,
    step,
    input,
    children,
    ...rest
  } = props;

  const formControl = useFormControl({
    id,
    "aria-label": rest["aria-label"],
    "aria-labelledby": rest["aria-labelledby"],
  });
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el || type !== "number") {
      return;
    }

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [type]);

  const setRefs = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    assignRef(ref, node);
    assignRef(input?.ref, node);
  };

  const iconLeft = !!icon && iconPosition === "left";
  const iconRight = !!icon && iconPosition !== "left";
  const iconInteractive = isInteractiveIcon(icon);
  const labelLeft = !!label && labelPosition !== "right";
  const labelRight = !!label && labelPosition === "right";
  const renderedIcon = isIconData(icon) ? (
    <Icon icon={icon as unknown as IconData} />
  ) : icon === true ? null : (
    (icon as React.ReactNode)
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(e, { value: e.target.value, name: name ?? e.target.name });
  };

  const borderColor = error ? "#e0b4b4" : "rgba(34, 36, 38, 0.15)";
  const color = error ? "#9f3a38" : "rgba(0, 0, 0, 0.87)";

  return (
    <UIProvider value={{ size }}>
      <Wrapper
        className={cx(
          fluid && "is-fluid",
          disabled && "is-disabled",
          `label-${labelPosition}`,
          className,
        )}
        style={style}
        $size={size}
      >
        {labelLeft ? label : null}
        <div style={{ position: "relative", flex: 1, display: "flex" }}>
          {iconLeft && renderedIcon ? (
            <IconSlot className="is-left" $interactive={iconInteractive}>
              {renderedIcon}
            </IconSlot>
          ) : null}
          <Field
            ref={setRefs}
            name={name}
            type={type}
            value={value}
            defaultValue={defaultValue}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            tabIndex={tabIndex}
            maxLength={maxLength}
            min={min}
            max={max}
            step={step}
            onChange={handleChange}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onKeyDownCapture={onKeyDownCapture}
            onKeyPress={onKeyPress}
            className={cx(
              iconLeft && "is-icon-left",
              (iconRight || !!loading) && "is-icon-right",
              labelLeft && "is-has-label",
              (!!action || labelRight) && "is-has-action",
            )}
            $borderColor={borderColor}
            $color={color}
            {...(rest as object)}
            {...formControl}
          />

          {iconRight && renderedIcon && !loading ? (
            <IconSlot $interactive={iconInteractive}>{renderedIcon}</IconSlot>
          ) : null}
          {loading ? <Spinner /> : null}
          {children}
        </div>
        {labelRight ? label : null}
        {action ? <ActionSlot>{action}</ActionSlot> : null}
      </Wrapper>
    </UIProvider>
  );
};

export const Input = styled(InputComponent)``;
